<template>
  <div id="achievements">
    <section class="competitions-block">
      <div class="hero-bg">
        <video class="hero-video-fill" autoplay muted loop playsinline>
          <source src="@/assets/video/competitions.mp4" type="video/mp4">
        </video>
        <div class="hero-overlay" />
        <div class="hero-grain" />
      </div>

      <v-container class="hero-content">
        <div class="hero-top">
          <span class="brand-mark">AGH SOLAR BOAT</span>
        </div>

        <div class="hero-mid">
          <h1>{{ $t('homepage.competitions.title') }}</h1>
        </div>

        <div class="hero-countries">
          <div class="hc-label">
            <span class="hc-line">{{ $t('homepage.achievements.compete_in') }}</span>
            <span class="hc-count">{{ $t('homepage.achievements.compete_places', { count: competitionsList.length }) }}</span>
          </div>
          <ul class="hc-grid">
            <li
              v-for="(comp, idx) in competitionsList"
              :key="comp.key"
              class="hc-item"
            >
              <span class="hc-num">{{ String(idx + 1).padStart(2, '0') }}</span>
              <span class="hc-country">{{ $t(comp.key + '.country') }}</span>
              <span class="hc-city">{{ $t(comp.key + '.city') }}</span>
            </li>
          </ul>
        </div>
      </v-container>
    </section>

    <section class="achievements-block">
      <v-container>
        <div class="chapter-eyebrow">
          <span class="ch-label">{{ $t('homepage.achievements.history_label') }}</span>
          <span class="ch-meta">{{ $t('homepage.achievements.history_meta') }}</span>
        </div>

        <h2>{{ $t('homepage.achievements.title') }}</h2>

        <div class="years-viewer">
          <nav class="year-tabs" :aria-label="$t('homepage.achievements.seasons_aria')">
            <button
              v-for="(group, idx) in groupedAchievements"
              :key="group.year"
              class="year-tab"
              :class="{ active: idx === currentYearIndex }"
              @click="currentYearIndex = idx"
            >
              {{ group.year }}
            </button>
          </nav>

          <transition mode="out-in" :css="false" @enter="onEnter" @leave="onLeave">
            <article
              :key="currentYearGroup.year"
              class="year-block"
            >
              <header class="table-caption">
                <span class="cap-col cap-col-place">{{ $t('homepage.achievements.table_place') }}</span>
                <span class="cap-col cap-col-title">{{ $t('homepage.achievements.table_competition') }}</span>
                <span class="cap-col cap-col-meta">
                    <span class="cap-sub">{{ $t('homepage.achievements.table_boat') }}</span>
                    <span class="cap-sub">{{ $t('homepage.achievements.table_location') }}</span>
                </span>
              </header>

              <ol class="result-rows">
                <li
                  v-for="(item, idx) in currentYearGroup.items"
                  :key="currentYearGroup.year + '-' + idx"
                  class="result-row"
                  :class="{ 'is-podium': item.position > 0 && item.position <= 3, 'is-special': item.position <= 0 }"
                >
                  <span class="r-place">
                    <template v-if="item.position > 0">
                      <span class="place-num">{{ item.position }}</span>
                      <span class="place-suffix">{{ placeSuffix(item.position) }}</span>
                    </template>
                    <template v-else-if="item.position === 0">
                      <span class="place-tag">{{ awardLabel(item) }}</span>
                    </template>
                    <template v-else-if="item.position === -1">
                      <span class="place-tag">{{ $t('homepage.achievements.construction') }}</span>
                    </template>
                    <template v-else-if="item.position === -2">
                      <span class="place-tag">{{ $t('homepage.achievements.kokos_water') }}</span>
                    </template>
                    <template v-else-if="item.position === -3">
                      <span class="place-tag">{{ $t('homepage.achievements.kokos_vehicle') }}</span>
                    </template>
                  </span>

                  <span class="r-title">{{ item.title }}</span>

                  <span class="r-meta">
                    <span v-if="item.boat" class="r-meta-line">
                      <span class="r-meta-label">{{ $t('homepage.achievements.table_boat') }}</span>
                      <span class="r-boat">{{ item.boat }}</span>
                    </span>
                    <span v-if="item.country" class="r-meta-line">
                      <span class="r-meta-label">{{ $t('homepage.achievements.table_location') }}</span>
                      <span class="r-country">{{ $t(item.country) }}</span>
                    </span>
                  </span>
                </li>
              </ol>
            </article>
          </transition>
        </div>
      </v-container>
    </section>
  </div>
</template>

<script>
import achievements from '@/achievements.js'
import anime from '../../../node_modules/animejs/lib/anime.es.js'

export default {
  name: "AchievementsComponent",
  data() {
    return {
      timelineItems: achievements,
      currentYearIndex: 0,
      competitionsList: [
        { key: 'competitions.monte-carlo' },
        { key: 'competitions.usa' },
        { key: 'competitions.balaton' },
        { key: 'competitions.sardynia' },
        { key: 'competitions.reguengos' },
        { key: 'competitions.wildau' },
        { key: 'competitions.akkrum' },
        { key: 'competitions.enkuizen' }
      ]
    }
  },
  computed: {
    groupedAchievements() {
      const groups = {}
      const order = []
      for (const item of achievements) {
        if (!groups[item.year]) {
          groups[item.year] = []
          order.push(item.year)
        }
        if (item.title || item.position !== undefined) {
          groups[item.year].push(item)
        }
      }
      return order
        .filter(y => groups[y].length > 0)
        .map(y => ({ year: y, items: groups[y] }))
    },
    currentYearGroup() {
      return this.groupedAchievements[this.currentYearIndex] || { year: '', items: [] }
    }
  },
  methods: {
    awardLabel(item) {
      return this.$t(item.year === 2026
        ? 'homepage.achievements.innovation_prize'
        : 'homepage.achievements.awarded')
    },
    onEnter(el, done) {
      anime({
        targets: el,
        translateY: [30, 0],
        opacity: [0, 1],
        easing: 'easeOutQuart',
        duration: 500,
        complete: done
      })
    },
    onLeave(el, done) {
      anime({
        targets: el,
        translateY: [0, -20],
        opacity: [1, 0],
        easing: 'easeInQuart',
        duration: 300,
        complete: done
      })
    },
    placeSuffix(pos) {
      if (this.$i18n.locale === 'pl') {
        return ' miejsce'
      }
      const lastDigit = pos % 10
      const lastTwo = pos % 100
      if (lastTwo >= 11 && lastTwo <= 13) return 'th'
      if (lastDigit === 1) return 'st'
      if (lastDigit === 2) return 'nd'
      if (lastDigit === 3) return 'rd'
      return 'th'
    }
  }
}
</script>

<style lang="sass" scoped>
  @media (min-width: 1904px)
    .container
      max-width: 1185px !important

  #achievements
    background: #faf7f1
    color: #0a0828

  .competitions-block
    position: relative
    color: #fff
    overflow: hidden
    display: flex
    width: 100%
    background: #0a0828

    .hero-bg
      position: absolute
      inset: 0
      z-index: 0
      overflow: hidden
      background: #0a0828

      .hero-video-fill
        position: absolute
        top: 50%
        left: 50%
        min-width: 100%
        min-height: 100%
        width: auto
        height: auto
        max-width: none
        transform: translate(-50%, -50%)
        object-fit: cover
        display: block
        filter: blur(4px) saturate(1.15) brightness(0.78) contrast(1.05)

      .hero-overlay
        position: absolute
        inset: 0
        background: linear-gradient(180deg, rgba(10, 8, 40, 0.5) 0%, rgba(10, 8, 40, 0.5) 30%, rgba(10, 8, 40, 0.7) 75%, rgba(10, 8, 40, 0.96) 100%)

      .hero-grain
        position: absolute
        inset: 0
        pointer-events: none
        opacity: 0.35
        mix-blend-mode: overlay
        background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 0.55 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>")
        background-repeat: repeat
        background-size: 160px 160px

    .hero-content
      position: relative
      z-index: 2
      display: flex
      flex-direction: column
      width: 100%
      padding: 110px 0 110px

    .hero-top
      display: flex
      align-items: baseline
      gap: 28px
      padding-bottom: 18px
      border-bottom: 1px solid rgba(255, 255, 255, 0.2)

      .brand-mark
        font-family: Inter, sans-serif
        font-weight: 800
        font-size: 0.95em
        letter-spacing: 0.18em
        color: #fff

      .brand-meta
        font-family: Inter, sans-serif
        font-weight: 500
        font-size: 0.82em
        letter-spacing: 0.04em
        color: rgba(255, 255, 255, 0.7)

    .hero-mid
      display: flex
      align-items: center
      padding: 70px 0 50px

    h1
      font-family: Raleway, sans-serif
      font-weight: 900
      font-size: clamp(2.2rem, 11vw, 8em)
      line-height: 0.92
      letter-spacing: -0.045em
      text-transform: uppercase
      color: #fff
      margin: 0
      text-rendering: geometricPrecision

    .hero-countries
      display: grid
      grid-template-columns: 220px 1fr
      gap: 56px
      align-items: start
      padding-top: 32px
      border-top: 1px solid rgba(255, 255, 255, 0.2)

      .hc-label
        display: flex
        flex-direction: column
        gap: 6px

        .hc-line
          font-family: Inter, sans-serif
          font-weight: 700
          font-size: 0.78em
          letter-spacing: 0.22em
          text-transform: uppercase
          color: rgba(255, 255, 255, 0.65)

        .hc-count
          font-family: Raleway, sans-serif
          font-weight: 800
          font-size: 1.1em
          line-height: 1.2
          letter-spacing: -0.01em
          color: #fff

      .hc-grid
        list-style: none
        padding: 0
        margin: 0
        display: grid
        grid-template-columns: repeat(4, 1fr)
        gap: 32px 28px

      .hc-item
        display: flex
        flex-direction: column
        gap: 4px
        padding-top: 4px
        border-top: 1px solid rgba(255, 255, 255, 0.25)

        .hc-num
          font-family: Inter, sans-serif
          font-weight: 700
          font-size: 0.7em
          letter-spacing: 0.22em
          color: rgba(255, 255, 255, 0.55)
          margin-bottom: 4px

        .hc-country
          font-family: Raleway, sans-serif
          font-weight: 800
          font-size: 1.05em
          line-height: 1.1
          letter-spacing: -0.015em
          text-transform: uppercase
          color: #fff

        .hc-city
          font-family: Inter, sans-serif
          font-weight: 500
          font-size: 0.78em
          letter-spacing: 0.04em
          color: rgba(255, 255, 255, 0.7)

  .achievements-block
    padding: 130px 0
    background: #faf7f1

    .chapter-eyebrow
      display: flex
      align-items: baseline
      gap: 32px
      margin-bottom: 50px

      .ch-label
        font-family: Inter, sans-serif
        font-weight: 700
        font-size: 0.78em
        letter-spacing: 0.22em
        text-transform: uppercase
        color: #0a0828

      .ch-meta
        font-family: Inter, sans-serif
        font-weight: 500
        font-size: 0.78em
        letter-spacing: 0.16em
        text-transform: uppercase
        color: #5b637e
        margin-left: auto

    h2
      font-family: Raleway, sans-serif
      font-weight: 900
      font-size: clamp(2rem, 10vw, 5em)
      line-height: 0.9
      letter-spacing: -0.045em
      text-transform: uppercase
      color: #0a0828
      margin: 0 0 clamp(48px, 8vw, 90px)
      max-width: 900px

  .years-viewer
    position: relative
    min-height: 600px

  .year-block
    padding-top: 8px

  .table-caption
    display: grid
    grid-template-columns: 220px 1.2fr 1fr
    align-items: baseline
    gap: 32px
    padding: 0 0 16px
    border-bottom: 2px solid #0a0828

    .cap-col
      font-family: Inter, sans-serif
      font-weight: 700
      font-size: 0.74em
      letter-spacing: 0.22em
      text-transform: uppercase
      color: rgba(10, 8, 40, 0.55)

    .cap-col-meta
      display: grid
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr)
      align-items: baseline
      gap: 16px

  .result-rows
    list-style: none
    padding: 0
    margin: 0

  .result-row
    display: grid
    grid-template-columns: 220px 1.2fr 1fr
    align-items: baseline
    gap: 32px
    padding: 24px 0
    border-bottom: 1px solid rgba(10, 8, 40, 0.12)
    transition: background 0.25s ease, padding 0.25s ease

    .r-place
      display: flex
      align-items: baseline
      gap: 8px
      font-family: Raleway, sans-serif
      color: rgba(10, 8, 40, 0.55)

      .place-num
        font-weight: 900
        font-size: 2.3em
        line-height: 1
        letter-spacing: -0.03em

      .place-suffix
        font-family: Inter, sans-serif
        font-weight: 600
        font-size: 0.78em
        letter-spacing: 0.16em
        text-transform: uppercase

      .place-tag
        font-family: Inter, sans-serif
        font-weight: 700
        font-size: 0.82em
        letter-spacing: 0.16em
        text-transform: uppercase
        color: rgba(10, 8, 40, 0.7)

    .r-title
      font-family: Raleway, sans-serif
      font-weight: 700
      font-size: 1.35em
      line-height: 1.25
      letter-spacing: -0.015em
      color: #0a0828

    .r-meta
      display: grid
      grid-template-columns: minmax(0, 1fr) minmax(0, 1.4fr)
      align-items: baseline
      gap: 16px
      font-family: Inter, sans-serif
      font-size: 0.92em
      color: #5b637e

      .r-meta-line
        display: contents

      .r-meta-label
        display: none

      .r-boat
        font-weight: 600
        color: #2a2750
        line-height: 1.3

      .r-country
        font-weight: 400
        line-height: 1.3

    &.is-podium
      .r-place
        color: #0a0828
        .place-num
          color: #0a0828
        .place-suffix
          color: #0a0828

    &.is-special
      .r-title
        color: #2a2750

  .year-tabs
    display: flex
    flex-wrap: wrap
    gap: 4px
    margin-bottom: 40px
    padding-bottom: 20px
    border-bottom: 1px solid rgba(10, 8, 40, 0.12)

    .year-tab
      font-family: Raleway, sans-serif
      font-weight: 800
      font-size: 1.4em
      line-height: 1
      letter-spacing: -0.02em
      color: rgba(10, 8, 40, 0.35)
      background: transparent
      border: none
      padding: 12px 22px
      cursor: pointer
      transition: all 0.25s ease
      position: relative

      &:hover
        color: rgba(10, 8, 40, 0.75)

      &.active
        color: #fff
        background: #0a0828
        font-weight: 900

  @media only screen and (max-width: 1180px)
    .competitions-block
      .hero-countries
        grid-template-columns: 1fr
        gap: 24px

        .hc-grid
          grid-template-columns: repeat(4, 1fr)

    .result-row
      grid-template-columns: 160px 1.3fr 1fr

  @media only screen and (max-width: 900px)
    .competitions-block
      .hero-content
        padding: clamp(70px, 12vw, 80px) 16px

      .hero-mid
        padding: clamp(32px, 6vw, 50px) 0 clamp(24px, 4vw, 36px)

      .hero-countries
        padding-top: 24px

        .hc-grid
          grid-template-columns: repeat(2, 1fr)
          gap: 24px 20px

        .hc-item
          .hc-country
            font-size: 0.95em
          .hc-city
            font-size: 0.72em

    .achievements-block
      padding: clamp(70px, 10vw, 90px) 0

      .chapter-eyebrow
        gap: 14px
        margin-bottom: 36px
        flex-wrap: wrap

        .ch-label, .ch-meta
          font-size: 0.7em
          letter-spacing: 0.18em

        .ch-meta
          margin-left: 0
          width: 100%

      h2
        margin-bottom: clamp(36px, 6vw, 60px)

    .years-viewer
      min-height: 500px

    .year-tabs
      gap: 2px
      margin-bottom: 28px
      padding-bottom: 16px

      .year-tab
        font-size: 1.05em
        padding: 8px 14px

  @media only screen and (max-width: 768px)
    .years-viewer
      min-height: 0

    .table-caption
      display: none

    .year-tabs
      flex-wrap: nowrap
      overflow-x: auto
      -webkit-overflow-scrolling: touch
      scrollbar-width: none
      margin-right: -12px
      padding-right: 12px

      &::-webkit-scrollbar
        display: none

      .year-tab
        flex-shrink: 0
        font-size: 0.95em
        padding: 8px 12px

    .result-rows
      display: flex
      flex-direction: column
      gap: 10px

    .result-row
      display: flex
      flex-direction: column
      align-items: flex-start
      gap: 10px
      padding: 16px 18px
      background: #fff
      border: 1px solid rgba(10, 8, 40, 0.1)
      border-radius: 2px

      .r-place
        width: 100%

        .place-num
          font-size: 1.6em

        .place-suffix
          font-size: 0.7em

        .place-tag
          font-size: 0.72em
          line-height: 1.35

      .r-title
        font-size: 1.05em
        line-height: 1.35
        width: 100%

      .r-meta
        width: 100%
        display: flex
        flex-direction: column
        gap: 10px
        padding-top: 10px
        border-top: 1px solid rgba(10, 8, 40, 0.08)

        .r-meta-line
          display: flex
          flex-direction: column
          gap: 3px

        .r-meta-label
          display: block
          font-family: Inter, sans-serif
          font-size: 0.65em
          font-weight: 700
          letter-spacing: 0.18em
          text-transform: uppercase
          color: rgba(10, 8, 40, 0.45)

        .r-boat,
        .r-country
          font-size: 0.88em
          line-height: 1.4

      &.is-podium
        border-left: 3px solid #0a0828

  @media only screen and (max-width: 599px)
    .competitions-block
      .hero-content
        padding-left: 14px
        padding-right: 14px

      .hero-top
        flex-direction: column
        align-items: flex-start
        gap: 8px

      h1
        font-size: 2.8em

</style>